/**
 * Shared task utilities
 */
import { toast } from 'react-toastify';

const normalizeRawTicketNumber = (rawTicketNumber) => {
  if (rawTicketNumber === undefined || rawTicketNumber === null) {
    return '';
  }

  const normalizedTicketNumber = String(rawTicketNumber).trim();
  if (!normalizedTicketNumber) {
    return '';
  }

  // Subtask codes can include a breadcrumb-like parent path. We only need the current ticket number.
  return normalizedTicketNumber.includes('>')
    ? normalizedTicketNumber.split('>').pop().trim()
    : normalizedTicketNumber;
};

const normalizeFocusedCommentId = (focusedCommentId) => {
  if (focusedCommentId === undefined || focusedCommentId === null) {
    return '';
  }

  const normalizedCommentId = String(focusedCommentId).trim();
  if (!normalizedCommentId) {
    return '';
  }

  return normalizedCommentId;
};

export const buildTaskUrl = (task, focusedCommentId = null) => {
  const ticketNumber = getTaskTicketNumber(task);
  const commentId = normalizeFocusedCommentId(focusedCommentId);

  if (!ticketNumber) {
    return '';
  }

  const commentSegment = commentId ? `/focusedCommentId=${encodeURIComponent(commentId)}` : '';
  return `${window.location.origin}/task/${encodeURIComponent(ticketNumber)}${commentSegment}`;
};

export const getTaskTicketNumber = (...sources) => {
  for (const source of sources) {
    if (!source) continue;

    const rawTicketNumber = source.ticket_number ?? source.ticketNumber ?? source.code ?? source.number;
    const normalizedTicketNumber = normalizeRawTicketNumber(rawTicketNumber);
    if (!normalizedTicketNumber) continue;

    return normalizedTicketNumber;
  }

  return '';
};

export const formatTaskCode = (taskData, ticketDetails) => {
  return getTaskTicketNumber(ticketDetails, taskData) || 'N/A';
};

export const handleCopyTaskLink = (task, t, focusedCommentId = null) => {
  const taskUrl = buildTaskUrl(task, focusedCommentId);
  if (!taskUrl) {
    toast.error(t('taskViewModal.linkCopyFailed'));
    return;
  }

  const ticketNumber = getTaskTicketNumber(task);
  const linkText = `${task.title || 'Task'} - ${ticketNumber}`;
  
  // Copy URL for browser compatibility and rich text for other apps
  if (navigator.clipboard && window.ClipboardItem) {
    const htmlBlob = new Blob([`<a href="${taskUrl}">${linkText}</a>`], { type: 'text/html' });
    const textBlob = new Blob([taskUrl], { type: 'text/plain' });
    const clipboardItem = new ClipboardItem({
      'text/html': htmlBlob,
      'text/plain': textBlob
    });
    navigator.clipboard.write([clipboardItem]).then(() => {
      toast.success(t('taskViewModal.linkCopied'));
    }).catch(() => {
      // Fallback to URL only
      navigator.clipboard.writeText(taskUrl).then(() => {
        toast.success(t('taskViewModal.linkCopied'));
      }).catch(err => {
        toast.error(t('taskViewModal.linkCopyFailed'));
      });
    });
  } else {
    // Fallback for older browsers - copy URL
    navigator.clipboard.writeText(taskUrl).then(() => {
      toast.success(t('taskViewModal.linkCopied'));
    }).catch(err => {
      toast.error(t('taskViewModal.linkCopyFailed'));
    });
  }
};

export const handleCopyCommentLink = (task, t, commentId) => {
  const taskUrl = buildTaskUrl(task, commentId);
  if (!taskUrl) {
    toast.error(t('taskViewModal.commentLinkCopyFailed'));
    return;
  }

  const ticketNumber = getTaskTicketNumber(task);
  const linkText = `${task.title || 'Task'} - ${ticketNumber}`;

  if (navigator.clipboard && window.ClipboardItem) {
    const htmlBlob = new Blob([`<a href="${taskUrl}">${linkText}</a>`], { type: 'text/html' });
    const textBlob = new Blob([taskUrl], { type: 'text/plain' });
    const clipboardItem = new ClipboardItem({
      'text/html': htmlBlob,
      'text/plain': textBlob
    });
    navigator.clipboard.write([clipboardItem]).then(() => {
      toast.success(t('taskViewModal.commentLinkCopied'));
    }).catch(() => {
      navigator.clipboard.writeText(taskUrl).then(() => {
        toast.success(t('taskViewModal.commentLinkCopied'));
      }).catch(() => {
        toast.error(t('taskViewModal.commentLinkCopyFailed'));
      });
    });
  } else {
    navigator.clipboard.writeText(taskUrl).then(() => {
      toast.success(t('taskViewModal.commentLinkCopied'));
    }).catch(() => {
      toast.error(t('taskViewModal.commentLinkCopyFailed'));
    });
  }
};

export const handleCopyTaskTitle = (task, t) => {
  const title = (typeof task === 'string' ? task : (task?.title || '')).trim();
  if (!title) {
    toast.error(t ? t('taskViewModal.titleCopyFailed', 'Failed to copy title') : 'Failed to copy title');
    return;
  }

  const successMessage = t ? t('taskViewModal.titleCopied', 'Ticket title copied to clipboard') : 'Ticket title copied to clipboard';
  const errorMessage = t ? t('taskViewModal.titleCopyFailed', 'Failed to copy title') : 'Failed to copy title';

  const copyWithFallback = () => {
    try {
      if (typeof document === 'undefined' || !document?.body || typeof document.execCommand !== 'function') {
        toast.error(errorMessage);
        return;
      }
      const textArea = document.createElement('textarea');
      textArea.value = title;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      try {
        textArea.select();
        const successful = document.execCommand('copy');
        if (successful) {
          toast.success(successMessage);
        } else {
          toast.error(errorMessage);
        }
      } finally {
        if (document.body.contains(textArea)) {
          document.body.removeChild(textArea);
        }
      }
    } catch {
      toast.error(errorMessage);
    }
  };

  try {
    if (typeof navigator !== 'undefined' && navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(title).then(() => {
        toast.success(successMessage);
      }).catch(() => {
        copyWithFallback();
      });
    } else {
      copyWithFallback();
    }
  } catch {
    copyWithFallback();
  }
};

